import { useEffect, useState } from "react";
import {
    Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Grid, IconButton, Tooltip, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import { FormProvider, RHFFileField } from "../../common/hook-form";
import { apiConstants, getGeneric, post, postGeneric } from "../../common/constants";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { downloadFile } from "../../common/util";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";
import { C, SX, TYPE } from "../merchants/shared/tokens";
import EmptyState from "../merchants/shared/EmptyState";

// NdrAutomated.js (236 lines) and NprAutomated.js (235 lines) were the same
// component: normalising NDR/NPR left six differing lines, all incidental. This
// is that component once, with the two flows declared as data.
//
// Merging also fixes a real gap -- the NPR copy was missing the "please select
// merchant" validation the NDR copy had, so an NPR rule could be submitted with
// no merchant attached.
const FLOWS = {
    NDR: {
        title: 'NDR Auto-Rules',
        blurb: 'Upload the rule sheet that decides what happens automatically when a delivery fails for a merchant.',
        listApi: apiConstants.NDR_AUTOMATED_RULE_LIST,
        uploadApi: apiConstants.NDR_AUTOMATED_RULE_UPLOAD,
        downloadApi: apiConstants.NDR_AUTOMATED_RULE_DOWNLOAD,
        sampleFile: 'NDR_RULE_UPLOAD',
        downloadName: 'NDR_Automated_rule',
        removeTitle: 'Turn off automated NDR actions?',
        removeBody: 'Failed deliveries for this merchant will go back to manual handling. The uploaded rules are deactivated, not deleted.',
    },
    NPR: {
        title: 'NPR Auto-Rules',
        blurb: 'Upload the rule sheet that decides what happens automatically when a pickup fails for a merchant.',
        listApi: apiConstants.NPR_AUTOMATED_RULE_LIST,
        uploadApi: apiConstants.NPR_AUTOMATED_RULE_UPLOAD,
        downloadApi: apiConstants.NPR_AUTOMATED_RULE_DOWNLOAD,
        sampleFile: 'NPR_RULE_UPLOAD',
        downloadName: 'NPR_Automated_rule',
        removeTitle: 'Turn off automated NPR actions?',
        removeBody: 'Failed pickups for this merchant will go back to manual handling. The uploaded rules are deactivated, not deleted.',
    },
};

export default function AutomatedRuleUpload({ kind = 'NDR' }) {
    const flow = FLOWS[kind] || FLOWS.NDR;

    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [confirmFor, setConfirmFor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [reload, setReload] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        setIsLoading(true);
        getGeneric(flow.listApi)
            .then((res) => {
                const active = res.data?.responseObj?.filter((v) => v.status === "ACTIVE");
                setData(active || []);
            })
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload, flow.listApi]);

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            merchant: Yup.string().required('Please select a merchant'),
            file: Yup.mixed().required('Please choose a file to upload'),
        })),
        defaultValues: { merchant: '', file: null },
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = (form) => {
        setIsSubmitting(true);
        const formData = new FormData();
        formData.append('file', form?.file[0]);
        postGeneric(`${flow.uploadApi}?merchantOid=${form.merchant}&status=ACTIVE`, formData)
            .then((res) => {
                if (res.data?.status === "SUCCESS") {
                    enqueueSnackbar(`${kind} rules uploaded`, { variant: 'success' });
                    reset();
                    setReload((r) => r + 1);
                } else {
                    enqueueSnackbar(res.data?.errorMsg || `Could not upload the ${kind} rules. Check the file format and try again.`, { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar(err.response?.data?.message || `Could not upload the ${kind} rules. Check the file format and try again.`, { variant: 'error' });
            })
            .finally(() => setIsSubmitting(false));
    };

    const downloadUploaded = (merchantOid) => {
        post(`${flow.downloadApi}?merchantOid=${merchantOid}`)
            .then((res) => {
                if (res.data.status === "ERROR") {
                    enqueueSnackbar(res.data.errorMsg || `No ${kind} rules exist for this merchant yet.`, { variant: 'error' });
                } else {
                    downloadFile(res.data, flow.downloadName);
                }
            })
            .catch((err) => enqueueSnackbar(err.response?.data?.message || 'Could not download the rules. Try again.', { variant: 'error' }));
    };

    const confirmRemove = () => {
        postGeneric(`${flow.uploadApi}?merchantOid=${confirmFor}&status=INACTIVE`)
            .then(() => {
                enqueueSnackbar(`Automated ${kind} actions turned off`, { variant: 'success' });
                setReload((r) => r + 1);
            })
            .catch((err) => enqueueSnackbar(err.response?.data?.message || `Could not turn off automated ${kind} actions. Try again.`, { variant: 'error' }))
            .finally(() => setConfirmFor(null));
    };

    const columns = [
        { field: 'name', headerName: 'Merchant', headerAlign: 'left', align: 'left', hideSortIcons: true, flex: 2 },
        {
            field: 'status', headerName: 'Status', headerAlign: 'left', align: 'left', hideSortIcons: true, flex: 1,
            renderCell: () => (
                <Box component="span" sx={{ ...SX.pill, color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}` }}>
                    Active
                </Box>
            ),
        },
        {
            field: 'action', headerName: 'Actions', headerAlign: 'left', align: 'left', hideSortIcons: true, flex: 1,
            renderCell: ({ row }) => (
                <>
                    <Tooltip title="Download this merchant's rule sheet">
                        <IconButton onClick={() => downloadUploaded(row.merchantOid)} size="small" sx={{ color: C.accent }}>
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`Turn off automated ${kind} actions`}>
                        <IconButton onClick={() => setConfirmFor(row.merchantOid)} size="small" sx={{ color: C.danger }}>
                            <CancelIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loader />}
            <PageTitle>{flow.title}</PageTitle>

            <Dialog open={!!confirmFor} onClose={() => setConfirmFor(null)}>
                <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>{flow.removeTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={TYPE.rowHelp}>{flow.removeBody}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmFor(null)} sx={SX.ctaGhost}>Keep them on</Button>
                    <Button onClick={confirmRemove} variant="contained" sx={{ ...SX.cta, bgcolor: C.danger, '&:hover': { bgcolor: '#991b1b' } }}>
                        Turn off
                    </Button>
                </DialogActions>
            </Dialog>

            <Card elevation={0} sx={{ ...SX.card, mb: 2 }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${C.lineSoft}` }}>
                    <Typography sx={TYPE.sectionTitle}>Upload rules</Typography>
                    <Typography sx={{ ...TYPE.rowHelp, mt: 0.3, maxWidth: '68ch' }}>{flow.blurb}</Typography>
                </Box>
                <CardContent sx={{ pt: 2.5 }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}><MerchantFilterCached required /></Grid>
                            <Grid item xs={12} md={4}><RHFFileField name="file" required fileType={'.csv'} /></Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <LoadingButton loading={isSubmitting} variant="contained" size="small" type="submit" sx={SX.cta}>
                                        Upload
                                    </LoadingButton>
                                    <Button variant="outlined" size="small" onClick={() => reset()} sx={SX.ctaGhost}>
                                        Clear
                                    </Button>
                                    <DownloadSampleFile fileName={flow.sampleFile} label="SAMPLE" />
                                </Box>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            <Card elevation={0} sx={SX.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${C.lineSoft}`, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={TYPE.sectionTitle}>Merchants with {kind} automation on</Typography>
                    <Typography sx={{ ...TYPE.meta, fontVariantNumeric: 'tabular-nums' }}>{data.length}</Typography>
                </Box>
                <CardContent>
                    {(!isLoading && data.length === 0) ? (
                        <EmptyState
                            dense
                            title={`No merchant has ${kind} automation on yet`}
                            body={`Upload a rule sheet above to start handling failed ${kind === 'NDR' ? 'deliveries' : 'pickups'} automatically.`}
                        />
                    ) : (
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight
                            rows={(data || []).map((row, i) => ({ ...row, id: i }))}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            pagination
                            hideFooterPagination={false}
                            paginationMode="client"
                            rowHeight={56}
                            rowCount={(data || []).length}
                            getRowId={(row) => row.id}
                        />
                    )}
                </CardContent>
            </Card>
        </>
    );
}
